'use client';

import { Voucher as VoucherComponent } from '@pixum/voucher';
import type { VoucherProps, VoucherSources } from './types';
import { addVoucherToCart } from 'services/api/client';
import { getVoucherFromBrowserStorage, getVoucherFromUrl } from './utils';
import { PIXUM_VOUCHER_CODE_STORAGE_KEY } from '../../constants';
import { logger } from 'utils';

/**
 * Retrieves voucher code from specified sources in priority order.
 * Checks each source sequentially and returns the first valid voucher found.
 *
 * @param sources - Ordered array of sources to check for voucher code
 * @param props - Voucher component props containing potential code value
 * @returns The voucher code from the first successful source, or undefined if none found
 *
 * @example
 * // Check props first, then URL
 * const code = getVoucherFromSource(['props', 'url'], { code: 'DIRECT123', ... });
 * // Returns: 'DIRECT123' (from props)
 *
 * @example
 * // Check URL first (when props.code is undefined)
 * const code = getVoucherFromSource(['url', 'localStorage'], { code: undefined, ... });
 * // Returns: URL voucher if present, otherwise falls back to localStorage
 */
function getVoucherFromSource(sources: VoucherSources, props: VoucherProps): string | undefined | null {
  // Iterate over sources and return the first valid voucher code found
  for (const source of sources) {
    if (source === 'props' && props.code) {
      return props.code;
    }

    if (source === 'url') {
      const result = getVoucherFromUrl();

      if (result) {
        return result;
      }
    }

    if (source === 'localStorage') {
      const result = getVoucherFromBrowserStorage(PIXUM_VOUCHER_CODE_STORAGE_KEY, true);

      if (result) {
        return result;
      }
    }
  }

  return undefined;
}

/**
 * Voucher component that displays voucher codes and optionally adds them to cart when copied.
 *
 * The component can retrieve voucher codes from multiple sources (props, URL parameters, localStorage)
 * in a configurable priority order. When addToCart is enabled, copying the voucher automatically
 * adds it to the shopping cart.
 *
 * @param props - Voucher component properties
 * @returns Rendered voucher component
 *
 * @example
 * // Basic voucher with direct code
 * <Voucher
 *   code="SUMMER2024"
 *   variant="default"
 *   addToCart={false}
 * />
 *
 * @example
 * // Voucher that checks URL first, then localStorage, with auto-add to cart
 * <Voucher
 *   variant="compact"
 *   addToCart={true}
 *   sources={['url', 'localStorage']}
 * />
 *
 * @example
 * // Voucher with custom styling and cart integration
 * <Voucher
 *   code="WELCOME10"
 *   variant="highlight"
 *   addToCart={true}
 *   className="my-voucher-styles"
 * />
 */
export function Voucher(props: VoucherProps) {
  const code = getVoucherFromSource(props.sources || ['props'], props);

  // Create updated props with the resolved code
  const voucherProps = {
    ...props,
    code: code || props.code,
  };

  if (props.addToCart) {
    const onVoucherCopied = async () => {
      try {
        await addVoucherToCart(voucherProps.code);
      } catch (error) {
        console.error('Error adding voucher to cart:', error);
        logger.error({ error, code: voucherProps.code }, 'Error adding voucher to cart');
      }
    };

    return <VoucherComponent {...voucherProps} onVoucherCopied={onVoucherCopied} />;
  }

  return <VoucherComponent {...voucherProps} />;
}
